/**
 * Minimal klientrouter på History API.
 *
 * Krav (§4): navigation skal fungere ved direkte URL, ved genindlæsning og
 * ved almindelige klik. Serverens SPA-fallback leverer `index.html` for alle
 * appruter, og denne router matcher stien i browseren.
 *
 * Bruges i stedet for `react-router-dom`, fordi byggemiljøet ikke havde
 * npm-adgang. Fladen (`<Link>`, `useRoute`, `navigate`) er bevidst holdt tæt
 * på React Routers, så et skifte er mekanisk.
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react';

export interface RouteMatch {
  path: string;
  params: Record<string, string>;
  query: URLSearchParams;
}

type Listener = () => void;
const listeners = new Set<Listener>();

function notify(): void {
  for (const l of listeners) l();
}

/** Navigerer uden at genindlæse siden. */
export function navigate(to: string, options: { replace?: boolean } = {}): void {
  if (options.replace) window.history.replaceState({}, '', to);
  else window.history.pushState({}, '', to);
  notify();
  window.scrollTo(0, 0);
}

export function useLocation(): { pathname: string; search: string } {
  const [location, setLocation] = useState(() => ({
    pathname: window.location.pathname,
    search: window.location.search,
  }));

  useEffect(() => {
    const update = (): void =>
      setLocation({ pathname: window.location.pathname, search: window.location.search });
    listeners.add(update);
    window.addEventListener('popstate', update);
    return () => {
      listeners.delete(update);
      window.removeEventListener('popstate', update);
    };
  }, []);

  return location;
}

/**
 * Matcher stien mod et mønster med `:param`-segmenter.
 * Returnerer `null`, hvis den ikke matcher.
 */
export function matchPath(pattern: string, pathname: string): Record<string, string> | null {
  const p = pattern.split('/').filter(Boolean);
  const a = pathname.split('/').filter(Boolean);
  if (p.length !== a.length) return null;
  const params: Record<string, string> = {};
  for (let i = 0; i < p.length; i++) {
    if (p[i].startsWith(':')) {
      params[p[i].slice(1)] = decodeURIComponent(a[i]);
      continue;
    }
    if (p[i] !== a[i]) return null;
  }
  return params;
}

export interface LinkProps {
  to: string;
  children: ReactNode;
  className?: string;
  'aria-current'?: string;
  [key: string]: unknown;
}

/**
 * Link der navigerer i appen. Almindelige modificerede klik
 * (Ctrl/Cmd/midterklik) overlades til browseren, så «åbn i ny fane» virker.
 */
export function Link({ to, children, ...rest }: LinkProps): JSX.Element {
  const onClick = useCallback(
    (event: MouseEvent): void => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }
      event.preventDefault();
      navigate(to);
    },
    [to],
  );

  return (
    <a href={to} onClick={onClick} {...rest}>
      {children}
    </a>
  );
}
